import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

import Section from "@/components/layout/Section";
import Container from "@/components/layout/Container";

import CalcVisual from "./CalcVisual";

import StepPropertyStatus from "./steps/StepPropertyStatus";
import StepAirbnbLink from "./steps/StepAirbnbLink";
import StepPropertyDetails from "./steps/StepPropertyDetails";
import StepCalculating from "./steps/StepCalculating";
import StepLeadCapture from "./steps/StepLeadCapture";
import StepSuccess from "./steps/StepSuccess";

import { saveLead } from "@/utils/saveLead";
import { fadeUp, stagger } from "../../../utils/motion";

const STEPS = {
  STATUS: 1,
  AIRBNB: 2,
  DETAILS: 3,
  CALC: 4,
  LEAD: 5,
  SUCCESS: 6,
};

function stageFromStep(step) {
  if (step <= 3) return 1;
  if (step === 4) return 2;
  return 3;
}

export default function CalcContainer() {
  const [step, setStep] = useState(STEPS.STATUS);
  const [data, setData] = useState({});
  const [visual, setVisual] = useState("hero_1");

  const stage = stageFromStep(step);
  const isMapMode = visual === "map";

  function update(patch) {
    setData((prev) => ({ ...prev, ...patch }));
  }

  function changeStep(nextStep, nextVisual) {
    if (nextVisual) setVisual(nextVisual);
    setStep(nextStep);
  }

  async function handleLeadSubmit(lead) {
    const finalLead = {
      page: "revenuecalc",
      leadType: data?.airbnbUrl ? "airbnb" : "manual",
      airbnbLink: data?.airbnbUrl || null,
      listingId: data?.listingId || null,
      propertyDetails: data?.airbnbUrl
        ? null
        : {
            location: data?.location || null,
            lat: data?.lat || null,
            lng: data?.lng || null,
            beds: data?.beds || null,
            baths: data?.baths || null,
          },
      leadDetails: lead,
    };

    await saveLead(finalLead);
    changeStep(STEPS.SUCCESS, "hero_2");
  }

  function startCalculation() {
    changeStep(STEPS.CALC, "analytics-live");
  }

  return (
    <Section background="bg-bg-surface" padding="lg">
      <Container>

        {step === STEPS.CALC && (
          <StepCalculating onDone={() => changeStep(STEPS.LEAD, "report")} />
        )}

        {step !== STEPS.CALC && (
          <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">

            {/* VISUAL */}
            <div className="order-1 lg:order-2">
              <motion.div
                key={visual}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35 }}
                className={`
                  relative w-full overflow-hidden rounded-[28px]
                  border border-brand-primary/15 shadow-card

                  h-[320px] sm:h-[380px]

                  ${
                    isMapMode
                      ? "md:aspect-[16/11] lg:min-h-[680px]"
                      : "md:aspect-[16/11] lg:min-h-[600px]"
                  }
                `}
                style={{
                  backgroundColor: "rgba(var(--brand-600-rgb), 0.98)",
                }}
              >
                <CalcVisual mode={visual} data={data} />
              </motion.div>
            </div>

            {/* CONTENT */}
            <motion.div
              variants={stagger()}
              initial="hidden"
              animate="show"
              className="order-2 lg:order-1 flex items-center"
            >
              <div className="max-w-content w-full">

                {/* PROGRESS */}
                <motion.div variants={fadeUp} className="mb-6">
                  <div className="flex items-center gap-3 text-small font-semibold">
                    <Stage label="Property" active={stage >= 1} />
                    <Divider active={stage >= 2} />
                    <Stage label="Analysis" active={stage >= 2} />
                    <Divider active={stage >= 3} />
                    <Stage label="Report" active={stage >= 3} />
                  </div>
                </motion.div>

                {/* STEPS */}
                <motion.div
                  key={step}
                  variants={fadeUp}
                  initial="hidden"
                  animate="show"
                >
                  {step === STEPS.STATUS && (
                    <StepPropertyStatus
                      onSelect={(value) => {
                        if (value === "airbnb") {
                          changeStep(STEPS.AIRBNB, "hero_2");
                        } else {
                          changeStep(STEPS.DETAILS, "map");
                        }
                      }}
                    />
                  )}

                  {step === STEPS.AIRBNB && (
                    <StepAirbnbLink
                      onChange={(d) => update(d)}
                      onNext={startCalculation}
                    />
                  )}

                  {step === STEPS.DETAILS && (
                    <StepPropertyDetails
                      data={data}
                      onChange={(d) => update(d)}
                      onNext={startCalculation}
                    />
                  )}

                  {step === STEPS.LEAD && (
                    <StepLeadCapture
                      payload={data}
                      onSubmit={handleLeadSubmit}
                    />
                  )}

                  {step === STEPS.SUCCESS && (
                    <StepSuccess
                      onReset={() => {
                        setStep(STEPS.STATUS);
                        setData({});
                        setVisual("hero_1");
                      }}
                    />
                  )}
                </motion.div>

              </div>
            </motion.div>

          </div>
        )}

      </Container>
    </Section>
  );
}

/* ---------- UI COMPONENTS ---------- */

function Stage({ label, active }) {
  return (
    <span
      className={`
        inline-flex items-center gap-1 rounded-full px-3 py-1
        text-micro font-semibold transition-all duration-300
        ${
          active
            ? "border border-brand-primary/15 bg-brand-secondary text-white"
            : "bg-bg-light text-text-muted"
        }
      `}
    >
      <span
        className={`
          h-1.5 w-1.5 rounded-full
          ${active ? "bg-accent-gold" : "bg-border-soft"}
        `}
      />
      {label}
    </span>
  );
}

function Divider({ active }) {
  return (
    <div className="flex flex-1 items-center gap-2">
      <div
        className={`
          h-[2px] flex-1 rounded-full
          ${active ? "bg-accent-gold" : "bg-border-soft"}
        `}
      />
      <ArrowRight
        size={14}
        className={active ? "text-accent-gold" : "text-text-muted"}
      />
    </div>
  );
}